/**
 * Registry mapping a RudderStack destination **definition name** to display
 * metadata (label + brand mark), plus a resolver with a generic fallback.
 *
 * Mirrors `settings/components/warehouseAccount/warehouseTypes.tsx`
 * (`warehouseTypeMeta`): the same problem, already solved once — brand glyphs
 * keyed off a backend string we neither own nor control. The key here is the
 * definition name (`FB_CUSTOM_AUDIENCE`, `TIKTOK_AUDIENCE`), which is what every
 * activation surface already carries: the wizard's allowlist entry, and
 * `destination.definitionName` on the wire for the list and run details.
 *
 * The fallback is not hypothetical. The wizard creates only the types in the
 * shared destination registry, but the activations list is workspace-wide and
 * shows syncs built in the webapp too — `MARKETO_STATIC_LIST` and
 * `GOOGLE_ADWORDS_REMARKETING_LISTS` among them. An unknown key must therefore
 * resolve to a real glyph, never a blank or a broken `<img>`.
 */
import type * as React from 'react';

import {
  BrazeAudienceIcon,
  GainsightPxAudienceIcon,
  GenericDestinationIcon,
  GoogleAdsAudienceIcon,
  HubSpotAudienceIcon,
  IterableAudienceIcon,
  LinkedInAudienceIcon,
  MarketoAudienceIcon,
  MetaAudienceIcon,
  PinterestAudienceIcon,
  RedditAudienceIcon,
  TikTokAudienceIcon,
} from './DestinationTypeIcon';

/** Display-only metadata for *any* destination definition name. */
export interface DestinationTypeDisplay {
  /** Human-facing type name ("Facebook Custom Audience"). */
  label: string;
  /** Brand mark (or the generic fallback). Accepts `className`. */
  Icon: React.ComponentType<{ className?: string }>;
}

/**
 * The definition names we ship a brand mark for. Deliberately a lookup table and
 * not a list: consumers resolve by key, and the wizard's ordered allowlist
 * (`lib/activationDestinationTypes.ts`) owns display order.
 */
const DESTINATION_TYPE_META: Record<string, DestinationTypeDisplay> = {
  BRAZE_AUDIENCE: { label: 'Braze Audience', Icon: BrazeAudienceIcon },
  FB_CUSTOM_AUDIENCE: { label: 'Facebook Custom Audience', Icon: MetaAudienceIcon },
  GAINSIGHT_PX_AUDIENCE: { label: 'Gainsight PX Audience', Icon: GainsightPxAudienceIcon },
  GOOGLE_ADWORDS_REMARKETING_LISTS: { label: 'Google Ads Remarketing List', Icon: GoogleAdsAudienceIcon },
  HS_AUDIENCE: { label: 'HubSpot Audience', Icon: HubSpotAudienceIcon },
  ITERABLE_AUDIENCE: { label: 'Iterable Audience', Icon: IterableAudienceIcon },
  LINKEDIN_AUDIENCE: { label: 'LinkedIn Audience', Icon: LinkedInAudienceIcon },
  MARKETO_AUDIENCE: { label: 'Marketo Audience', Icon: MarketoAudienceIcon },
  PINTEREST_AUDIENCE: { label: 'Pinterest Audience', Icon: PinterestAudienceIcon },
  REDDIT_AUDIENCE: { label: 'Reddit Audience', Icon: RedditAudienceIcon },
  TIKTOK_AUDIENCE: { label: 'TikTok Audience', Icon: TikTokAudienceIcon },
};

/** `ITERABLE_AUDIENCE` → `Iterable Audience`. Readable, and never invents a brand. */
function prettifyDefinitionName(definitionName: string): string {
  const words = definitionName
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  return words.length > 0 ? words.join(' ') : 'Destination';
}

/**
 * Resolve a destination definition name to its display metadata.
 *
 * Accepts `null` / `undefined` because the caller's value may be absent (a sync
 * whose destination failed to project, a wizard step before a type is chosen) —
 * those degrade to the generic mark rather than forcing every call site to guard.
 */
export function destinationTypeIcon(definitionName: string | null | undefined): DestinationTypeDisplay {
  const key = (definitionName ?? '').toUpperCase();
  const found = DESTINATION_TYPE_META[key];
  if (found) return found;
  return { label: prettifyDefinitionName(key), Icon: GenericDestinationIcon };
}
